import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Bell, Check, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import AddRecordModal from '../../../components/AddRecordModal/AddRecordModal';
import { useAuth } from '../../../context/AuthContext';
import { useIntl } from 'react-intl';
import { useOverviewCurrency } from '../../../context/OverviewCurrencyContext';
import {
	CURRENCIES_WITH_FLAGS,
	getCurrencyFlag,
} from '../../../constants/currencies';
import { useNotifications } from '../../../entities/notification';
import NotificationDropdown from '../../../components/NotificationDropdown/NotificationDropdown';
import { useOnClickOutside } from '../../../shared/lib/hooks/use-on-click-outside';
import { useOverviewRefresh } from '../../../context/OverviewRefreshContext';

import styles from './sidebar-header.module.css';

export const SidebarHeader = () => {
	const intl = useIntl();
	const { fetchMe, userInfo: currentUser } = useAuth();

	const { currency: overviewCurrency, setCurrency: setOverviewCurrency } =
		useOverviewCurrency();

	// Use full currency list for dropdown; ensure current value is included if not in list
	const currencyOptions = [...CURRENCIES_WITH_FLAGS];
	if (!currencyOptions.some(c => c.code === overviewCurrency)) {
		currencyOptions.unshift({
			code: overviewCurrency,
			flag: getCurrencyFlag(overviewCurrency),
			name: overviewCurrency,
		});
	}
	const { requestRefresh } = useOverviewRefresh();
	const {
		notifications,
		pendingCount,
		isLoading: notificationsLoading,
		declineNotification,
		markAsRead,
		refreshNotifications,
	} = useNotifications();

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [pendingAcceptNotification, setPendingAcceptNotification] =
		useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [refreshTrigger, setRefreshTrigger] = useState(0);
	const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] =
		useState(false);
	const [isCurrencyDropdownOpen, setIsCurrencyDropdownOpen] = useState(false);
	const [currencyDropdownRect, setCurrencyDropdownRect] = useState(null);
	const notificationDropdownRef = useRef(null);
	const currencyTriggerRef = useRef(null);

	const [formData, setFormData] = useState({
		fullName: '',
		login: '',
		email: '',
		password: '',
		dateOfBirth: '',
		phoneNumber: '',
		phoneCode: '+374',
		language: '',
		currency: '',
	});
	const hasLoadedData = useRef(false);

	useEffect(() => {
		if (!currentUser) {
			fetchMe();
		}
	}, [currentUser, fetchMe]);

	const refreshData = () => {
		setRefreshTrigger(prev => prev + 1);
	};

	const getProfilePictureUrl = profilePicture => {
		if (!profilePicture) return null;

		if (
			profilePicture.startsWith('http://') ||
			profilePicture.startsWith('https://')
		) {
			return profilePicture;
		}

		const API_URL = process.env.REACT_APP_API_URL;
		let baseUrl = API_URL?.endsWith('/') ? API_URL.slice(0, -1) : API_URL;

		baseUrl = baseUrl.replace(/\/api\/v\d+$/, '').replace(/\/api$/, '');

		if (profilePicture.startsWith('/uploads/')) {
			return `${baseUrl}${profilePicture}`;
		}

		// If it's a relative path without /uploads/, add it
		if (!profilePicture.startsWith('/')) {
			return `${baseUrl}/uploads/${profilePicture}`;
		}

		return profilePicture;
	};

	// Get user initials as fallback
	const getUserInitials = () => {
		if (currentUser?.full_name) {
			const names = currentUser.full_name.trim().split(' ');
			if (names.length >= 2) {
				return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
			}
			return currentUser.full_name.substring(0, 2).toUpperCase();
		}
		return 'U';
	};

	useEffect(() => {
		if (hasLoadedData.current) {
			return;
		}

		const loadUserData = async () => {
			try {
				setIsLoading(true);

				// Check if we have a token first
				const token = localStorage.getItem('accessToken');
				if (!token) {
					console.error('No authentication token found');
					setIsLoading(false);
					return;
				}

				// Try to get user data from context first
				let userData = currentUser;

				// If no user data in context, try to fetch it
				if (!userData) {
					userData = await fetchMe();
				}

				if (userData) {
					setFormData({
						fullName: userData.full_name || '',
						currency: userData.currency_preference || 'USD',
					});
					hasLoadedData.current = true;
				} else {
					console.warn('No user data available');
				}
			} catch (error) {
				console.error('Failed to load user data:', error);
			} finally {
				setIsLoading(false);
			}
		};

		loadUserData();
	}, [fetchMe, currentUser, setIsLoading]);

	// Handle click outside to close notification dropdown
	useOnClickOutside(notificationDropdownRef, () => {
		setIsNotificationDropdownOpen(false);
	});

	// Position currency dropdown (portal): below trigger, left-aligned with button
	useLayoutEffect(() => {
		if (!isCurrencyDropdownOpen || !currencyTriggerRef.current) {
			setCurrencyDropdownRect(null);
			return;
		}
		const rect = currencyTriggerRef.current.getBoundingClientRect();
		setCurrencyDropdownRect({
			top: rect.bottom + 8,
			left: rect.left,
			width: rect.width,
		});
		return () => setCurrencyDropdownRect(null);
	}, [isCurrencyDropdownOpen]);

	// Handle Escape key to close dropdown
	useEffect(() => {
		const handleEscape = e => {
			if (e.key === 'Escape' && isNotificationDropdownOpen) {
				setIsNotificationDropdownOpen(false);
			}
		};
		document.addEventListener('keydown', handleEscape);
		return () => document.removeEventListener('keydown', handleEscape);
	}, [isNotificationDropdownOpen]);

	const handleNotificationClick = () => {
		setIsNotificationDropdownOpen(prev => !prev);
	};

	const handleAccept = notificationId => {
		const notification = notifications.find(n => n.id === notificationId);
		if (!notification || notification.notification_type !== 'recurring') return;
		setPendingAcceptNotification({ id: notification.id, ...notification });
		setIsModalOpen(true);
		setIsNotificationDropdownOpen(false);
	};

	const handleDecline = async notificationId => {
		try {
			await declineNotification(notificationId);
		} catch (error) {
			console.error('Failed to decline notification:', error);
		}
	};

	const handleMarkAsRead = async notificationId => {
		try {
			await markAsRead(notificationId);
		} catch (error) {
			console.error('Failed to mark notification as read:', error);
		}
	};

	const profilePictureUrl = getProfilePictureUrl(currentUser?.profile_picture);
	const userInitials = getUserInitials();
	const userName = currentUser?.full_name || formData.fullName || 'User';

	return (
		<header className={styles.header}>
			<div className={styles.breadcrumb}></div>
			<div className={styles.headerActions}>
				<div className={styles.searchWrapper}>
					<Link
						to='/statement-imports'
						className={styles.importStatementButton}
					>
						{intl.formatMessage({ id: 'StatementImportIDHeaderButton' })}
					</Link>
				</div>
				<button
					className={styles.addNewButton}
					onClick={() => {
						setPendingAcceptNotification(null);
						setIsModalOpen(true);
					}}
				>
					{intl.formatMessage({ id: 'HomeSubHeader' })}
					<span className={styles.addPlus} aria-hidden>
						+
					</span>
				</button>
				<div
					className={styles.currencyDropdownWrapper}
					ref={currencyTriggerRef}
				>
					<button
						type='button'
						className={styles.currencyDropdownTrigger}
						onClick={() => setIsCurrencyDropdownOpen(prev => !prev)}
						aria-label='Select currency'
						aria-expanded={isCurrencyDropdownOpen}
						aria-haspopup='listbox'
					>
						<span className={styles.currencyDropdownFlag} aria-hidden>
							{getCurrencyFlag(overviewCurrency)}
						</span>
						<span className={styles.currencyDropdownValue}>
							{overviewCurrency}
						</span>
						<ChevronDown
							size={16}
							strokeWidth={2}
							className={styles.currencyDropdownChevron}
							aria-hidden
						/>
					</button>
					{isCurrencyDropdownOpen && (
						<>
							{currencyDropdownRect ? (
								createPortal(
									<>
										<div
											className={styles.currencyDropdownBackdrop}
											onClick={() => setIsCurrencyDropdownOpen(false)}
											aria-hidden
										/>
										<div
											className={styles.currencyDropdownPanelPortal}
											style={{
												position: 'fixed',
												top: currencyDropdownRect.top,
												left: currencyDropdownRect.left,
												right: 'auto',
												minWidth: currencyDropdownRect.width,
											}}
											role='listbox'
											aria-label='Currency options'
										>
											{currencyOptions.map(c => (
												<button
													key={c.code}
													type='button'
													role='option'
													aria-selected={c.code === overviewCurrency}
													className={`${styles.currencyDropdownOption} ${
														c.code === overviewCurrency
															? styles.currencyDropdownOptionActive
															: ''
													}`}
													onClick={() => {
														setOverviewCurrency(c.code);
														setIsCurrencyDropdownOpen(false);
													}}
												>
													<span
														className={styles.currencyDropdownFlag}
														aria-hidden
													>
														{c.flag || getCurrencyFlag(c.code)}
													</span>
													<span>{c.code}</span>
													{c.code === overviewCurrency && (
														<Check
															size={16}
															className={styles.currencyDropdownCheck}
														/>
													)}
												</button>
											))}
										</div>
									</>,
									document.body,
								)
							) : (
								<>
									<div
										className={styles.currencyDropdownBackdrop}
										onClick={() => setIsCurrencyDropdownOpen(false)}
										aria-hidden
									/>
									<div
										className={styles.currencyDropdownPanel}
										role='listbox'
										aria-label='Currency options'
									>
										{currencyOptions.map(c => (
											<button
												key={c.code}
												type='button'
												role='option'
												aria-selected={c.code === overviewCurrency}
												className={`${styles.currencyDropdownOption} ${
													c.code === overviewCurrency
														? styles.currencyDropdownOptionActive
														: ''
												}`}
												onClick={() => {
													setOverviewCurrency(c.code);
													setIsCurrencyDropdownOpen(false);
												}}
											>
												<span>{c.code}</span>
												{c.code === overviewCurrency && (
													<Check
														size={16}
														className={styles.currencyDropdownCheck}
													/>
												)}
											</button>
										))}
									</div>
								</>
							)}
						</>
					)}
				</div>
				<div
					className={styles.notificationContainer}
					ref={notificationDropdownRef}
				>
					<div
						className={styles.notificationIconWrapper}
						onClick={handleNotificationClick}
					>
						<Bell size={20} className={styles.iconBtn} />
						{pendingCount > 0 && (
							<span className={styles.notificationBadge}>
								{pendingCount > 99 ? '99+' : pendingCount}
							</span>
						)}
					</div>
					{isNotificationDropdownOpen && (
						<NotificationDropdown
							notifications={notifications}
							onAccept={handleAccept}
							onDecline={handleDecline}
							onMarkAsRead={handleMarkAsRead}
							isLoading={notificationsLoading}
						/>
					)}
				</div>
				<Link to='/profile' className={styles.userProfileLink}>
					<div className={styles.userProfileContainer}>
						{profilePictureUrl ? (
							<img
								src={profilePictureUrl}
								alt={userName}
								className={styles.userAvatarImage}
							/>
						) : (
							<div className={styles.userAvatar}>{userInitials}</div>
						)}
						<span className={styles.userName}>{userName}</span>
					</div>
				</Link>
			</div>

			<AddRecordModal
				open={isModalOpen}
				onClose={() => {
					setIsModalOpen(false);
					setPendingAcceptNotification(null);
					refreshNotifications();
				}}
				onSuccess={() => {
					requestRefresh();
					refreshData();
				}}
				defaultTab={
					pendingAcceptNotification?.recurring_type === 'expense'
						? 'expense'
						: 'income'
				}
				prefillFromRecurringNotification={pendingAcceptNotification}
			/>
		</header>
	);
};
